import {
  Component,
  ChangeDetectionStrategy,
  signal,
  inject,
  computed,
  AfterViewInit,
  ElementRef,
} from '@angular/core';
import { DecimalPipe, DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProductDetailsGallery } from './product-details-gallery/product-details-gallery';
import { ProductReviews } from './product-reviews/product-reviews';
import {
  ShamRatingStarIcon,
  ShamHeartIcon,
  ShamShare08Icon,
  ShamTruckIcon,
  ShamMedalIcon,
  ShamHandshakeIcon,
  ShamHeadphonesIcon,
  ShamCreditCardIcon,
  ShamFavourite01Icon,
  ShamStarIcon,
} from 'sham-components/icons';
import { imagePlaceholderUrl } from 'sham-components/shared';
import { FrequentProducts } from './frequent-products/frequent-products';
import { SameSellerProducts } from './same-seller-products/same-seller-products';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import {
  ContentApi,
  SiteProductDetailsResponse,
  SiteProductVariantOption,
  SiteProductVariant,
} from '@/app/services/content-api';
import { firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { CartApi } from '@/app/services/cart-api';
import { ProductReviewsManager } from './product-reviews/product-reviews-manager';
import { SiteLanguage } from '@/app/services/website-context';
import { LanguageService } from 'sham-components/services';
import { ToastService } from '@/app/services/http-error-notification.service';

export interface OptionGroup {
  nameId: string;
  name: string;
  values: { valueId: string; value: string }[];
}

@Component({
  selector: 'app-product-details',
  imports: [
    FormsModule,
    ProductDetailsGallery,
    ProductReviews,
    ShamRatingStarIcon,
    ShamHeartIcon,
    ShamShare08Icon,
    ShamTruckIcon,
    ShamMedalIcon,
    ShamHandshakeIcon,
    ShamHeadphonesIcon,
    ShamCreditCardIcon,
    FrequentProducts,
    SameSellerProducts,
    ShamFavourite01Icon,
    ShamStarIcon,
    RouterLink,
    TranslateModule,
    DecimalPipe,

  ],
  templateUrl: './product-details.html',
  styleUrl: './product-details.css',
})
export class ProductDetails implements AfterViewInit {
  private api = inject(ContentApi);
  private cartApi = inject(CartApi);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private reviewsManager = inject(ProductReviewsManager);
  private document = inject(DOCUMENT);
  private toastService = inject(ToastService);
  languageService = inject(LanguageService);

  product = signal<SiteProductDetailsResponse | null>(null);
  hasFrequentProducts = signal(true);
  hasSameSellerProducts = signal(true);
  frequentProductsTotal = signal(0);
  attributes = signal<OptionGroup[]>([]);
  isLoading = signal(true);
  slug = signal<string>('');

  selectedAttributes = signal<Record<string, string>>({});
  quantity = signal(1);
  readonly MIN_QUANTITY = 1;

  private el = inject(ElementRef);
  private observer!: IntersectionObserver;

  matchedVariant = computed<SiteProductVariant | null>(() => {
    const prod = this.product();
    if (!prod || !prod.variants) return null;
    const selected = this.selectedAttributes();

    return (
      prod.variants.find((v) => {
        return v.options.every((opt) => selected[opt.nameId] === opt.valueId);
      }) || null
    );
  });

  activePriceAmount = computed(() => {
    const variant = this.matchedVariant();
    if (variant) {
      return variant.isOnSale && variant.salePrice
        ? variant.salePrice
        : variant.regularPrice;
    }
    const prod = this.product();
    if (!prod) return 0;
    return prod.onSale && prod.salePrice ? prod.salePrice : prod.regularPrice;
  });

  activeSalesInfo = computed(() => {
    const variant = this.matchedVariant();
    if (variant) {
      return variant.isOnSale && variant.regularPrice > (variant.salePrice || 0)
        ? {
            amount: variant.regularPrice,
            percentage: Math.round(
              ((variant.regularPrice - (variant.salePrice || 0)) /
                variant.regularPrice) *
                100,
            ),
          }
        : null;
    }
    const prod = this.product();
    if (!prod) return null;
    return prod.onSale && prod.regularPrice > (prod.salePrice || 0)
      ? {
          amount: prod.regularPrice,
          percentage:
            prod.discountPercentage ||
            Math.round(
              ((prod.regularPrice - (prod.salePrice || 0)) / prod.regularPrice) * 100,
            ),
        }
      : null;
  });

  activeStockStatus = computed(() => {
    const variant = this.matchedVariant();
    if (variant) return variant.hasStock && variant.availableQuantity > 0;
    const product = this.product();
    return !!product?.hasStock && product.availableQuantity > 0;
  });

  maxQuantity = computed(() => {
    const variant = this.matchedVariant();
    if (variant) return variant.hasStock ? variant.availableQuantity : 0;
    const product = this.product();
    return product?.hasStock ? product.availableQuantity : 0;
  });

  canPurchase = computed(
    () => this.activeStockStatus() && this.quantity() >= this.MIN_QUANTITY,
  );

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const slugValue = params.get('slug');
      if (slugValue) {
        this.hasFrequentProducts.set(true);
        this.hasSameSellerProducts.set(true);
        this.frequentProductsTotal.set(0);
        this.slug.set(slugValue);
        void this.loadProduct(slugValue);
      }
    });
  }

  getCurrentLanguage(): SiteLanguage {
    return this.languageService.currentLang() === 'ar' ? 'ar' : 'en';
  }

  setHasFrequentProducts(hasProducts: boolean) {
    this.hasFrequentProducts.set(hasProducts);
  }

  setFrequentProductsTotal(total: number) {
    this.frequentProductsTotal.set(total);
  }

  setHasSameSellerProducts(hasProducts: boolean) {
    this.hasSameSellerProducts.set(hasProducts);
  }

  async loadProduct(slug: string) {
    this.isLoading.set(true);
    try {
      const response = await firstValueFrom(
        this.api.getProductBySlug(slug, this.getCurrentLanguage()),
      );
      if (response) {
        this.product.set(response);
        this.reviewsManager.setSummary(
          response.reviewsAverage,
          response.reviewsCount,
        );
        setTimeout(() => {
          this.observeElements();
        });
        // Parse attributes
        const groups = new Map<string, OptionGroup>();
        for (const v of response.variants) {
          for (const opt of v.options) {
            if (!groups.has(opt.nameId)) {
              groups.set(opt.nameId, {
                nameId: opt.nameId,
                name: opt.name,
                values: [],
              });
            }
            const group = groups.get(opt.nameId)!;
            if (!group.values.find((val) => val.valueId === opt.valueId)) {
              group.values.push({ valueId: opt.valueId, value: opt.value });
            }
          }
        }

        const attrsArray = Array.from(groups.values());
        this.attributes.set(attrsArray);

        const initSelection: Record<string, string> = {};
        for (const attr of attrsArray) {
          if (attr.values.length > 0) {
            initSelection[attr.nameId] = attr.values[0].valueId;
          }
        }
        this.selectedAttributes.set(initSelection);

        this.syncQuantityToStock();
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  isColorGroup(groupName: string): boolean {
    const v = (groupName || '').trim().toLowerCase();
    return v === 'color' || v.includes('color') || v === 'اللون' || v.includes('لون');
  }

  getSwatchColor(value: string): string {
    const raw = (value || '').trim();
    if (!raw) return '#9ca3af';

    if (/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(raw)) {
      return raw;
    }

    if (/^(rgb|hsl)a?\(/i.test(raw)) {
      return raw;
    }

    const lower = raw.toLowerCase();
    const map: Record<string, string> = {
      black: '#111827',
      white: '#ffffff',
      gray: '#6b7280',
      grey: '#6b7280',
      red: '#ef4444',
      blue: '#3b82f6',
      green: '#22c55e',
      yellow: '#f59e0b',
      orange: '#f97316',
      purple: '#8b5cf6',
      pink: '#ec4899',
      brown: '#92400e',
      cyan: '#06b6d4',
      teal: '#14b8a6',
      navy: '#1e3a8a',
      beige: '#f5f5dc',

      أسود: '#111827',
      أبيض: '#ffffff',
      ابيض: '#ffffff',
      رمادي: '#6b7280',
      رصاصي: '#6b7280',
      أحمر: '#ef4444',
      احمر: '#ef4444',
      أزرق: '#3b82f6',
      ازرق: '#3b82f6',
      أخضر: '#22c55e',
      اخضر: '#22c55e',
      أصفر: '#f59e0b',
      اصفر: '#f59e0b',
      برتقالي: '#f97316',
      بنفسجي: '#8b5cf6',
      ارجواني: '#8b5cf6',
      وردي: '#ec4899',
      زهري: '#ec4899',
      بني: '#92400e',
      سماوي: '#06b6d4',
      تركوازي: '#14b8a6',
      كحلي: '#1e3a8a',
      نيفي: '#1e3a8a',
    };

    return map[lower] ?? null;
  }

  selectAttribute(attrCode: string, attrValueCode: string) {
    this.selectedAttributes.update((current) => ({
      ...current,
      [attrCode]: attrValueCode,
    }));
    this.syncQuantityToStock();
  }

  getSelectedValue(attrCode: string): string | undefined {
    return this.selectedAttributes()[attrCode];
  }

  incrementQuantity() {
    if (!this.activeStockStatus()) return;
    this.quantity.update((current) =>
      current < this.maxQuantity() ? current + 1 : current,
    );
  }

  decrementQuantity() {
    if (!this.activeStockStatus()) return;
    this.quantity.update((current) =>
      current > this.MIN_QUANTITY ? current - 1 : current,
    );
  }

  onQuantityChange(value: number | string) {
    if (!this.activeStockStatus()) {
      this.quantity.set(0);
      return;
    }

    const num = Number(value);
    if (Number.isFinite(num)) {
      const clamped = Math.max(this.MIN_QUANTITY, Math.min(this.maxQuantity(), num));
      this.quantity.set(clamped);
    }
  }

  addToCart() {
    if (!this.canPurchase()) return;
    const item = this.selectedCartItem();
    if (!item) return;

    this.cartApi.addToCart(item.productId, item.variantId, item.quantity).subscribe({
      next: () => this.showToast('Added to cart', 'success'),
      error: () => this.showToast('Unable to add item to cart', 'error'),
    });
  }

  buyNow() {
    if (!this.canPurchase()) return;
    const item = this.selectedCartItem();
    if (!item) return;

    this.cartApi.addToCart(item.productId, item.variantId, item.quantity).subscribe({
      next: () => void this.router.navigate(['/cart']),
    });
  }

  addToWishlist() {
    const item = this.selectedCartItem();
    if (!item) return;

    this.cartApi.addToWishlist(item.productId, item.variantId).subscribe({
      next: () => this.showToast('Added to favorites', 'success'),
      error: () => this.showToast('Unable to add item to favorites', 'error'),
    });
  }

  async copyProductLink() {
    const url = this.document.location.href;
    const clipboard = this.document.defaultView?.navigator.clipboard;

    if (clipboard) {
      try {
        await clipboard.writeText(url);
        this.showToast('Link copied successfully', 'success');
        return;
      } catch {
        // Fall back for non-secure origins where Clipboard API is blocked.
      }
    }

    const input = this.document.createElement('input');
    input.value = url;
    input.setAttribute('readonly', '');
    this.document.body.append(input);
    input.select();
    const copied = this.document.execCommand('copy');
    input.remove();
    this.showToast(copied ? 'Link copied successfully' : 'Unable to copy link', copied ? 'success' : 'error');
  }

  private selectedCartItem() {
    const product = this.product();
    const variant = this.matchedVariant();
    if (!product || !variant) return null;

    return {
      productId: product.id,
      variantId: variant.id,
      quantity: this.quantity(),
    };
  }

  private syncQuantityToStock() {
    this.quantity.set(this.activeStockStatus() ? this.MIN_QUANTITY : 0);
  }

  private showToast(message: string, type: 'success' | 'error') {
    this.toastService.show(message, type);
  }

  private initObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -80px 0px',
      },
    );
  }

  private observeElements() {
    const elements = this.el.nativeElement.querySelectorAll('.reveal:not(.observed)');

    elements.forEach((el: HTMLElement) => {
      el.classList.add('observed');
      this.observer.observe(el);
    });
  }
  ngAfterViewInit() {
    this.initObserver();
  }

  goToSellerProfile(sellerId?: string) {
    if (!sellerId) return;

    this.router.navigate(['/seller', sellerId]);
  }

  activeImages = computed(() => {
    const product = this.product();
    if (!product) return [];

    const generalMedia = product.media || [];
    let finalMedia = [...generalMedia];

    const variant = this.matchedVariant();

    if (variant) {
      const matchedMediaGroup = product.variantValueMedia?.find((group) =>
        variant.options.some((opt) => opt.valueId === group.attributeValueId),
      );

      if (matchedMediaGroup?.media?.length) {
        finalMedia = [...matchedMediaGroup.media, ...generalMedia];
      } else if (variant.primaryImageUrl) {
        finalMedia = [
          {
            url: variant.primaryImageUrl,
            isVideo: false,
            sortOrder: 0,
            isPrimary: true,
          } as any,
          ...generalMedia,
        ];
      }
    }

    const uniqueMedia = Array.from(
      new Map(finalMedia.map((item) => [item.url, item])).values(),
    );

    return uniqueMedia;
  });
}
